/**
 * generateTransactionPDF.js
 *
 * Pure-JavaScript PDF generation for invoices and receipts.
 * Uses jsPDF + jspdf-autotable — no backend required.
 *
 * Public exports:
 *   downloadInvoicePDF({ transaction, customer, lineItems, payments, type })
 *   downloadReceiptPDF({ transaction, customer, lineItems, payments, type })
 *   getReceiptBlob({ transaction, customer, lineItems, payments, type })
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ── Company constants ──────────────────────────────────────────────────────────
const CO_NAME    = 'The Jewelry Doctors of Auburndale Inc';
const CO_PHONE   = '(863) 604-9085';
const CO_ADDRESS = '1052 US 92 | Booth C-46';
const CO_CITY    = 'Auburndale, FL 33823';

// ── Formatters ─────────────────────────────────────────────────────────────────
const fmt$ = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
        .format(Number(amount ?? 0));

const fmtDate = (value) => {
    if (!value) return 'N/A';
    try {
        return new Date(value).toLocaleString('en-US', {
            dateStyle: 'medium',
            timeStyle: 'short',
        });
    } catch {
        return 'N/A';
    }
};

const hasVal = (v) =>
    v !== null && v !== undefined && String(v).trim() !== '';

// ── Resolve customer name ──────────────────────────────────────────────────────
function resolveCustomerName(transaction, customer) {
    return (
        transaction.customerName ||
        (customer
            ? customer.isBusiness
                ? customer.firstName || 'N/A'
                : `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'N/A'
            : 'N/A')
    );
}

// ── Header ─────────────────────────────────────────────────────────────────────
function drawHeader(doc, docTitle) {
    // Company block — left
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(20, 20, 20);
    doc.text(CO_NAME, 14, 20);

    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(90, 90, 90);
    doc.text(CO_PHONE,   14, 26);
    doc.text(CO_ADDRESS, 14, 31);
    doc.text(CO_CITY,    14, 36);

    // Document title — right
    doc.setFontSize(26);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 40, 40);
    doc.text(docTitle, 196, 24, { align: 'right' });

    // Divider
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.4);
    doc.line(14, 42, 196, 42);
    doc.setTextColor(0, 0, 0);

    return 50; // Y for next section
}

// ── Info grid (transaction + customer side by side) ────────────────────────────
function drawInfoGrid(doc, transaction, customer, type, startY) {
    const customerName = resolveCustomerName(transaction, customer);

    const leftPairs = [
        ['Ticket ID',   transaction.ticketId || transaction.transactionId || 'N/A'],
        ['Type',        type === 'sale' ? 'Sale' : 'Repair'],
        ['Status',      transaction.transactionStatus || 'N/A'],
        ['Date Opened', fmtDate(transaction.transactionDate)],
        ['Opened By',   transaction.employeeName || 'N/A'],
    ];
    if (hasVal(transaction.promisedDate)) {
        leftPairs.push(['Promised Date', fmtDate(transaction.promisedDate)]);
    }

    const rightPairs = [
        ['Customer', customerName],
        ['Email',    customer?.email || 'N/A'],
        ['Phone',    customer?.phoneNumber || customer?.phone || 'N/A'],
    ];

    const lh = 5.5;
    doc.setFontSize(9);

    leftPairs.forEach(([label, val], i) => {
        doc.setFont('helvetica', 'bold');
        doc.text(`${label}:`, 14, startY + i * lh);
        doc.setFont('helvetica', 'normal');
        doc.text(String(val), 52, startY + i * lh);
    });

    rightPairs.forEach(([label, val], i) => {
        doc.setFont('helvetica', 'bold');
        doc.text(`${label}:`, 115, startY + i * lh);
        doc.setFont('helvetica', 'normal');
        const lines = doc.splitTextToSize(String(val), 67);
        doc.text(lines, 138, startY + i * lh);
    });

    let y = startY + Math.max(leftPairs.length, rightPairs.length) * lh + 4;

    // Transaction notes (if any)
    if (hasVal(transaction.transactionNotes)) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Notes:', 14, y);
        doc.setFont('helvetica', 'normal');
        const noteLines = doc.splitTextToSize(transaction.transactionNotes, 165);
        doc.text(noteLines, 36, y);
        y += noteLines.length * 5 + 4;
    }

    return y + 2;
}

// ── Line items table ───────────────────────────────────────────────────────────
function drawLineItemsTable(doc, lineItems, startY) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(20, 20, 20);
    doc.text('Line Items', 14, startY);

    const rows = lineItems.map((item, i) => [
        item.id || String(i + 1),
        item.title       || '-',
        item.description || '-',
        item.material    || '-',
        hasVal(item.quantity)       ? String(item.quantity) : '1',
        fmt$(item.cost ?? 0),
        hasVal(item.discountAmount) && Number(item.discountAmount) !== 0
            ? fmt$(item.discountAmount)
            : '-',
        item.taxExempt ? 'Yes' : 'No',
    ]);

    autoTable(doc, {
        startY: startY + 5,
        head: [['Line #', 'Title', 'Description', 'Material', 'Qty', 'Cost', 'Discount', 'Tax Exempt']],
        body: rows.length > 0 ? rows : [['-', 'No line items', '', '', '', '', '', '']],
        theme: 'striped',
        headStyles: { fillColor: [50, 50, 50], textColor: 255, fontSize: 8 },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
            0: { cellWidth: 14 },
            1: { cellWidth: 28 },
            2: { cellWidth: 52 },
            3: { cellWidth: 22 },
            4: { cellWidth: 10 },
            5: { cellWidth: 22 },
            6: { cellWidth: 22 },
            7: { cellWidth: 12 },
        },
        margin: { left: 14, right: 14 },
    });

    return doc.lastAutoTable.finalY + 8;
}

// ── Financial summary (right-aligned) ─────────────────────────────────────────
/**
 * @param {boolean} isInvoice  true → show Amount Paid + BALANCE DUE box
 *                             false → show PAID IN FULL banner
 */
function drawFinancials(doc, transaction, isInvoice, startY) {
    const subtotal   = Number(transaction.transactionSubtotal ?? 0);
    const discount   = Number(transaction.transactionDiscount ?? 0);
    const tax        = Number(transaction.transactionTax ?? 0);
    const total      = Number(transaction.transactionTotal ?? 0);
    const balanceDue = Number(transaction.balanceDue ?? 0);
    const amountPaid = total - balanceDue;

    const summaryRows = [];
    if (subtotal  > 0) summaryRows.push(['Subtotal',  fmt$(subtotal)]);
    if (discount  > 0) summaryRows.push(['Discount',  `- ${fmt$(discount)}`]);
    if (tax       > 0) summaryRows.push(['Tax',       fmt$(tax)]);
    summaryRows.push(['Total', fmt$(total)]);
    if (isInvoice)     summaryRows.push(['Amount Paid', fmt$(amountPaid)]);

    const tblW = 82;
    const tblX = 196 - tblW;

    autoTable(doc, {
        startY,
        body: summaryRows,
        theme: 'plain',
        bodyStyles: { fontSize: 9 },
        columnStyles: {
            0: { fontStyle: 'bold', halign: 'left',  cellWidth: 50 },
            1: { halign: 'right',                    cellWidth: 32 },
        },
        tableWidth: tblW,
        margin: { left: tblX, right: 14 },
    });

    let y = doc.lastAutoTable.finalY + 2;

    if (isInvoice) {
        // Red BALANCE DUE pill
        doc.setFillColor(220, 53, 69);
        doc.roundedRect(tblX, y, tblW, 12, 2, 2, 'F');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text('BALANCE DUE',     tblX + 4,   y + 8);
        doc.text(fmt$(balanceDue),  196 - 4,    y + 8, { align: 'right' });
        doc.setTextColor(0, 0, 0);
        y += 18;
    } else {
        // Green PAID IN FULL banner (full width)
        y += 4;
        doc.setDrawColor(25, 135, 84);
        doc.setLineWidth(0.6);
        doc.roundedRect(14, y, 182, 14, 2, 2, 'S');
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(25, 135, 84);
        doc.text('PAID IN FULL', 105, y + 9.5, { align: 'center' });
        doc.setTextColor(0, 0, 0);
        y += 20;
    }

    return y;
}

// ── Payment history table ──────────────────────────────────────────────────────
function drawPaymentsTable(doc, payments, startY) {
    if (!payments || payments.length === 0) return startY;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(20, 20, 20);
    doc.text('Payment History', 14, startY);

    const rows = payments.map((p) => [
        fmtDate(p.paymentDate),
        p.paymentMethod || 'N/A',
        fmt$(p.baseAmount ?? p.amount),
        p.ccFeeApplied ? fmt$(p.ccFeeAmount) : '-',
        fmt$(p.amount),
        p.employeeName || 'N/A',
    ]);

    autoTable(doc, {
        startY: startY + 5,
        head: [['Date', 'Method', 'Base Amount', 'Platform Fee', 'Total Charged', 'Employee']],
        body: rows,
        theme: 'striped',
        headStyles: { fillColor: [50, 50, 50], textColor: 255, fontSize: 8 },
        bodyStyles: { fontSize: 8 },
        margin: { left: 14, right: 14 },
    });

    return doc.lastAutoTable.finalY + 6;
}

// ── Store policies section ─────────────────────────────────────────────────────
const STORE_POLICIES = [
    'No Refunds: All sales are final. No cash refunds will be issued under any circumstances.',
    'Exchanges: Merchandise may be exchanged for items of equal or greater value within 14 days ' +
        'of this finalized transaction date, accompanied by this original receipt. Custom-made or ' +
        'specifically sized jewelry is FINAL SALE — no exchanges, returns, or refunds will be accepted ' +
        'under any circumstances.',
    'Platform Fees: Online payments carry a 3% online platform fee to cover remote digital ' +
        'payment processing. In-store card payments may carry a 3% platform fee. ' +
        'Cash is always accepted at no additional charge.',
];

function drawPolicies(doc, startY) {
    const pageH       = doc.internal.pageSize.getHeight();
    const bottomMargin = 16; // reserve space for footer
    const sectionNeed  = 48; // estimated mm needed for the section

    let y = startY + 4;

    // Move to a new page if there isn't enough room
    if (y + sectionNeed > pageH - bottomMargin) {
        doc.addPage();
        y = 18;
    }

    // Thin separator line
    doc.setDrawColor(210, 210, 210);
    doc.setLineWidth(0.3);
    doc.line(14, y, 196, y);
    y += 5;

    // Section title
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(80, 80, 80);
    doc.text('STORE POLICIES', 14, y);
    y += 5;

    // Bullet policies
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);

    for (const policy of STORE_POLICIES) {
        const lines = doc.splitTextToSize(`* ${policy}`, 182);
        const needed = lines.length * 4 + 2;

        if (y + needed > pageH - bottomMargin) {
            doc.addPage();
            y = 18;
        }

        doc.text(lines, 14, y);
        y += needed;
    }

    doc.setTextColor(0, 0, 0);
    return y + 2;
}

// ── Footer (all pages) ─────────────────────────────────────────────────────────
function drawFooter(doc, generatedLabel) {
    const pages = doc.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
        doc.setPage(i);
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(160, 160, 160);
        doc.text('Thank you for your business!', 14, 289);
        doc.text(`Generated ${generatedLabel}  |  Page ${i} of ${pages}`, 196, 289, { align: 'right' });
        doc.setTextColor(0, 0, 0);
    }
}

// ── Document builders (shared logic) ──────────────────────────────────────────

function buildInvoiceDoc({ transaction, customer, lineItems, payments, type }) {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    let y = drawHeader(doc, 'INVOICE');
    y = drawInfoGrid(doc, transaction, customer, type, y);
    y = drawLineItemsTable(doc, lineItems ?? [], y);
    y = drawFinancials(doc, transaction, true, y);
    y = drawPaymentsTable(doc, payments ?? [], y);
    y = drawPolicies(doc, y);
    drawFooter(doc, fmtDate(new Date().toISOString()));
    return doc;
}

function buildReceiptDoc({ transaction, customer, lineItems, payments, type }) {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    let y = drawHeader(doc, 'RECEIPT');
    y = drawInfoGrid(doc, transaction, customer, type, y);
    y = drawLineItemsTable(doc, lineItems ?? [], y);
    y = drawFinancials(doc, transaction, false, y);
    y = drawPaymentsTable(doc, payments ?? [], y);
    y = drawPolicies(doc, y);
    drawFooter(doc, fmtDate(new Date().toISOString()));
    return doc;
}

// ── Public API ─────────────────────────────────────────────────────────────────

/**
 * Triggers an immediate browser download of an invoice PDF.
 */
export function downloadInvoicePDF(opts) {
    const doc = buildInvoiceDoc(opts);
    const id  = opts.transaction.ticketId || opts.transaction.transactionId || 'invoice';
    doc.save(`invoice-${id}.pdf`);
}

/**
 * Triggers an immediate browser download of a receipt PDF.
 */
export function downloadReceiptPDF(opts) {
    const doc = buildReceiptDoc(opts);
    const id  = opts.transaction.ticketId || opts.transaction.transactionId || 'receipt';
    doc.save(`receipt-${id}.pdf`);
}

/**
 * Returns the receipt as a Blob (for uploading to S3 without auto-download).
 */
export function getReceiptBlob(opts) {
    const doc = buildReceiptDoc(opts);
    return doc.output('blob');
}
